import { DOC_SEARCH_SELECT, useGeckoAnalytics } from 'features/analytics'
import type { CardFilterValues } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentCardFilterBar/types'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { buildDocSearchSelectPayload } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docSearchSelectAnalytics'
import type { DocumentSearchResult } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type { SearchActionsContextType } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import { usePowerAnalyticsBackend } from 'features/knowledgeDocumentViewer/hooks/usePowerAnalyticsBackend'
import { useCallback } from 'react'

type SearchNavigationTarget = {
  pageNumber?: number
  pageNum?: number
  fragmentText?: string
}

export interface UseDocumentSearchNavigationParams {
  activeTabId: string
  cardFilters: CardFilterValues
  documentResults: DocumentTableRow[]
  isGeckoUser: boolean
  onAddRecentDocument: SearchActionsContextType['onAddRecentDocument']
  onOpenNewTab: SearchActionsContextType['onOpenNewTab']
  onSelectDocument: SearchActionsContextType['onSelectDocument']
  onSnapshotSearchResults: SearchActionsContextType['onSnapshotSearchResults']
  rawSearchData: DocumentSearchResult | undefined
  searchQuery: string
  siteId: string
}

export function useDocumentSearchNavigation({
  activeTabId,
  cardFilters,
  documentResults,
  isGeckoUser,
  onAddRecentDocument,
  onOpenNewTab,
  onSelectDocument,
  onSnapshotSearchResults,
  rawSearchData,
  searchQuery,
  siteId,
}: UseDocumentSearchNavigationParams): (
  doc: DocumentTableRow,
  newTab: boolean,
  isLinkedDocsMode: boolean,
  target?: SearchNavigationTarget
) => void {
  const { trackEvent } = useGeckoAnalytics()
  const { sendPowerAnalyticsEvent } = usePowerAnalyticsBackend(siteId)

  return useCallback(
    (
      doc: DocumentTableRow,
      newTab: boolean,
      linkedDocsMode: boolean,
      target?: SearchNavigationTarget
    ) => {
      const pageNumber = target?.pageNumber ?? target?.pageNum
      const fragmentHighlight =
        target?.pageNum !== undefined && target.fragmentText?.trim()
          ? { pageNum: target.pageNum, fragmentText: target.fragmentText }
          : undefined
      const selectPayload = buildDocSearchSelectPayload({
        doc,
        documentResults,
        cardFilters,
        rawSearchData,
        query: searchQuery,
        siteId,
        openInNewTab: newTab,
        isLinkedDocsMode: linkedDocsMode,
      })
      trackEvent(DOC_SEARCH_SELECT.title, selectPayload)
      sendPowerAnalyticsEvent(DOC_SEARCH_SELECT.title, selectPayload)
      onAddRecentDocument(doc)
      if (isGeckoUser) {
        onSnapshotSearchResults(activeTabId, documentResults)
      }

      if (newTab) {
        onOpenNewTab({
          documentId: doc.id,
          pageNumber,
          fragmentHighlight,
          searchOriginQuery: searchQuery,
        })
      } else {
        onSelectDocument({
          documentId: doc.id,
          pageNumber,
          fragmentHighlight,
          searchOriginQuery: searchQuery,
        })
      }
    },
    [
      activeTabId,
      cardFilters,
      documentResults,
      isGeckoUser,
      onAddRecentDocument,
      onOpenNewTab,
      onSelectDocument,
      onSnapshotSearchResults,
      rawSearchData,
      searchQuery,
      sendPowerAnalyticsEvent,
      siteId,
      trackEvent,
    ]
  )
}
